import { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { useTranslation } from 'next-i18next';
import CloseIcon from '../../../public/asset/closeIcon.svg';
import { Button, Drawer, LinkBack, Typography } from '@/components/atoms';
import { SortCollection } from '@/components/molecules';
import {
  CategoriesListMobile,
  FiltersListMobile,
} from '@/components/organisms';
import { initSortSelected, collectionSortOptions } from '@/utils/Catalog';
import { filterValidQueryParams } from '@/utils/Filters';
import { ICmsTagsGroup, ICollectionSortOption } from '@/types/collections';
import { FiltersPanelMobileCmp } from './FiltersPanelMobileCmp';
import { capitalize, findItemNested, getLabel } from '@/utils/index';
import { FacetValue } from '@/lib/models/types/product';
interface StyledProps {
  show?: boolean;
  marginBottom?: boolean;
}
const ButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 85%;
  max-width: 480px;
  margin: 0 auto;
  padding: 8px 0;
`;
const Btn = styled(Button)`
  width: 48%;
  max-width: 200px;
  height: 48px;
  text-transform: uppercase;
`;

const UpperBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 400px;
  width: 100%;
  height: 56px;
  padding: 0 8px;
`;

const OpenDrawer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  cursor: pointer;
  border: 1px solid ${props => props.theme.paletteDefault.grey1};
  width: 180px;
  padding: 4px;
  font-size: 15px;
  text-align: center;

  ${props => props.theme.breakpoints.up('mobileL')} {
    font-size: 16px;
    width: 200px;
  }
`;

const LinkBackWrapper = styled.div<StyledProps>`
  display: ${props => (props.show ? 'flex' : 'none')};
  align-items: center;
  color: ${props => props.theme.paletteDefault.primary.main};
`;

const BottomContent = styled.div`
  width: 100%;
  flex-grow: 1;
  height: calc(100% - 120px);
  padding: 8px 0 0;
`;
const FiltersDrawerContent = styled.div`
  width: 100vw;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  ${props => props.theme.breakpoints.up('sm')} {
    max-width: 400px;
  }
`;

const CategoriesDrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 640px;
  height: 100%;
  max-height: 900px;
  margin: 0 auto;
  padding: 8px;
`;
const CloseIconWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0 4px;
`;

const DrawerTitle = styled.div`
  padding: 4px;
  width: 95%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Title = styled.div`
  padding: 8px 0;
  color: ${props => props.theme.paletteDefault.primary.main};
`;

const OuterDrawerContent = styled.div`
  display: flex;
  height: 100%;
`;

const Header = styled.div<StyledProps>`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  margin-bottom: ${props => (props.marginBottom ? '16px' : 0)};
`;

const SortWrapper = styled.div`
  width: 100%;
  font-size: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
  padding: 0 8px;
`;

const WrapperFiltersListMobile = styled.div`
  width: 100%;
  flex-grow: 1;
  padding: 8px 0;
  height: calc(100% - 360px);
`;

const Buttons = styled.div`
  position: sticky;
  bottom: 80px;
`;

const BtnTypography = styled(Typography)`
  line-height: 120%;
`;

const FiltersPanelMobile: FC<FiltersPanelMobileCmp> = ({
  cmsCollectionTags,
  filters,
  categoryPath,
  filterSelectionMobileHandler,
  type,
}) => {
  const { t } = useTranslation(['common', 'catalog']);
  const currency = useSelector(
    (state: RootState) => state.currencyReducer.currency
  );
  const storeCollectionInfo = useSelector(
    (state: RootState) => state.collectionInfoReducer.collectionInfo
  );

  const pathname =
    type === 'brands' ? '/brands/[slug]' : '/collections/[[...slug]]';

  const router = useRouter();

  let { slug, sort_by, ...queryParameters } = router.query;
  let validQueryParameters = filterValidQueryParams(queryParameters);

  const [selected, setSelected] = useState<string[]>(
    initCategoriesSelected(slug as string[])
  );
  const [filtersSelected, setFiltersSelected] = useState<{
    [k: string]: { slug: string; label: string };
  } | null>(
    initFiltersSelected(
      validQueryParameters,
      cmsCollectionTags?.filters,
      currency?.symbol || ''
    )
  );

  const [sortSelected, setSortSelected] = useState<{
    value: string;
    label: string;
  } | null>(initSortSelected(sort_by as string));

  useEffect(() => {
    let { slug, sort_by, ...queryParameters } = router.query;
    let validQueryParameters = filterValidQueryParams(queryParameters);
    setFiltersSelected(
      initFiltersSelected(
        validQueryParameters,
        cmsCollectionTags?.filters,
        currency?.symbol || ''
      )
    );
    setSelected(initCategoriesSelected(slug as string[]));
    setSortSelected(initSortSelected(sort_by as string));
  }, [router.asPath, currency?.symbol]);

  useEffect(() => {
    if (filtersSelected) {
      let queryParametersToSet: { [k: string]: string } = {};
      Object.keys(filtersSelected).forEach(filterName => {
        if (filtersSelected[filterName]) {
          queryParametersToSet[filterName] = filtersSelected[filterName].slug;
        }
      });
      filterSelectionMobileHandler(queryParametersToSet);
    }
  }, [filtersSelected]);

  const [bottomDrawerOpen, setBottomDrawerOpen] = useState(false);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);

  const closeDrawers = () => {
    if (bottomDrawerOpen) {
      setBottomDrawerOpen(false);
    }
    if (rightDrawerOpen) {
      setRightDrawerOpen(false);
    }
  };
  const resetCategories = () => {
    closeDrawers();
    let queryParametersToSet: { [k: string]: string } = {};
    if (filtersSelected) {
      Object.keys(filtersSelected).forEach(filterName => {
        if (filtersSelected[filterName]) {
          queryParametersToSet[filterName] = filtersSelected[filterName].slug;
        }
      });
    }
    const urlObj: {
      pathname: string;
      query: { [k: string]: string | string[] | undefined };
    } = {
      pathname,
      query: {
        slug: slug && slug[0] ? [slug[0]] : [],
        ...queryParametersToSet,
        sort_by: sortSelected?.value,
      },
    };

    delete urlObj.query.p;

    router.push(urlObj, undefined, { shallow: true });
  };
  const showResults = () => {
    closeDrawers();

    let queryParametersToSet: { [k: string]: string } = {};
    if (filtersSelected) {
      Object.keys(filtersSelected).forEach(filterName => {
        if (filtersSelected[filterName]) {
          queryParametersToSet[filterName] = filtersSelected[filterName].slug;
        }
      });
    }

    const urlObj: {
      pathname: string;
      query: { [k: string]: string | string[] | undefined };
    } = {
      pathname,
      query: {
        slug: selected,
        ...queryParametersToSet,
        sort_by: sortSelected?.value,
      },
    };

    delete urlObj.query.p;

    router.push(urlObj, undefined, { shallow: true });
  };
  const toggleBottomDrawer = (open: boolean) => {
    return () => {
      if (!open) {
        showResults();
      }
      setBottomDrawerOpen(open);
    };
  };

  const toggleRightDrawer = (open: boolean) => {
    return () => {
      if (!open) {
        showResults();
      }
      setRightDrawerOpen(open);
    };
  };

  const sortChangeHandler = (option: ICollectionSortOption): void => {
    setSortSelected(option);
  };

  const removeFilter = (filterName: string) => {
    const copyFiltersSelected = { ...filtersSelected };
    if (copyFiltersSelected[filterName]) {
      delete copyFiltersSelected[filterName];
    }
    setFiltersSelected({ ...copyFiltersSelected });
  };

  const resetAllFilters = () => {
    setFiltersSelected(null);
    setSortSelected(initSortSelected(null));
  };

  if (slug && !Array.isArray(slug)) {
    slug = [slug];
  }

  const genderLabel = getLabel(selected[0], cmsCollectionTags?.filters?.gender);

  let facetFound: { values: FacetValue[] } | null = null;
  if (selected.length === 1 && categoryPath) {
    facetFound = { values: categoryPath };
  }

  if (selected.length >= 2) {
    if (categoryPath) {
      facetFound = findItemNested(
        categoryPath,
        selected[selected.length - 1],
        'values'
      );
    }
  }

  let all = slug && slug.length > 0 ? selected : [];
  if (
    (facetFound?.values?.length === 0 || !facetFound?.values) &&
    categoryPath
  ) {
    facetFound = findItemNested(
      categoryPath,
      selected[selected.length - 2],
      'values'
    );

    if (facetFound) {
      all = selected.slice(0, selected.length - 1);
    } else {
      facetFound = { values: categoryPath };
      all = slug && slug.length > 0 ? (slug as string[]) : [];
    }
  }

  let backTitle = '';

  if (!all || all.length === 0) {
  } else {
    if (all && (all.length >= 3 || all.length > (slug?.length || 0))) {
      backTitle = 'Back';
    }
  }

  let parentTitle = '';

  if (!all || all.length === 0) {
    parentTitle = t('catalog:all-collections');
  } else {
    if (all.length === 1) {
      parentTitle = `All ${genderLabel} Categories`;
    } else {
      if (all.length >= 2) {
        parentTitle = `${genderLabel} ${getLabel(
          all[all.length - 1],
          cmsCollectionTags?.categories[all[1]]
        )}`;
      }
    }
  }

  const numFiltersSelected = filtersSelected
    ? Object.keys(filtersSelected).length
    : 0;
  return (
    <>
      <UpperBar>
        <OpenDrawer onClick={toggleBottomDrawer(true)}>
          <Typography component="p" datatype="body_1">
            {t('catalog:cta.browse-categories')}
          </Typography>
        </OpenDrawer>
        <OpenDrawer onClick={toggleRightDrawer(true)}>
          <Typography component="p" datatype="body_1">
            {`${t('catalog:cta.filter-sort')} ${
              numFiltersSelected ? `(${numFiltersSelected})` : ``
            }`}
          </Typography>
        </OpenDrawer>
      </UpperBar>
      <Drawer
        anchor="right"
        open={rightDrawerOpen}
        onClose={toggleRightDrawer(false)}
        transitionDuration={400}>
        <OuterDrawerContent>
          <FiltersDrawerContent>
            <Header marginBottom={false}>
              <DrawerTitle>
                <Typography component="h1">
                  {capitalize(storeCollectionInfo?.title || '')}
                </Typography>
              </DrawerTitle>
              <CloseIconWrapper>
                <CloseIcon onClick={toggleRightDrawer(false)} />
              </CloseIconWrapper>
            </Header>

            <WrapperFiltersListMobile>
              <FiltersListMobile
                cmsCollectionTagsFilters={cmsCollectionTags?.filters}
                filters={filters}
                filtersSelected={filtersSelected}
                filterSelectHandler={(selected: {
                  [k: string]: {
                    slug: string;
                    label: string;
                  };
                }) => {
                  setFiltersSelected(selected);
                }}
                removeFilterHandler={removeFilter}
              />
              <SortWrapper>
                <SortCollection
                  sortSelected={sortSelected}
                  isMobile
                  sortChangeHandler={sortChangeHandler}
                  collectionSortOptions={collectionSortOptions}
                />
              </SortWrapper>
            </WrapperFiltersListMobile>
            <Buttons>
              <ButtonsContainer>
                <Btn onClick={resetAllFilters} variant="outlined">
                  <BtnTypography datatype="body_1">
                    {t('catalog:cta.remove-filters').toUpperCase()}
                  </BtnTypography>
                </Btn>
                <Btn onClick={showResults}>
                  <BtnTypography datatype="body_1">
                    {t('catalog:cta.view-results').toUpperCase()}
                  </BtnTypography>
                </Btn>
              </ButtonsContainer>
            </Buttons>
          </FiltersDrawerContent>
        </OuterDrawerContent>
      </Drawer>

      <Drawer
        anchor="bottom"
        open={bottomDrawerOpen}
        onClose={toggleBottomDrawer(false)}
        transitionDuration={400}
        classes={{
          //@ts-ignore
          paperAnchorBottom: {
            height: '100%',
          },
        }}>
        <OuterDrawerContent>
          <CategoriesDrawerContent>
            <Header>
              <DrawerTitle>
                <Typography component="h1" style={{ fontWeight: 600 }}>
                  {t('catalog:cta.browse-categories').toUpperCase()}
                </Typography>
              </DrawerTitle>
              <CloseIconWrapper>
                <CloseIcon onClick={toggleBottomDrawer(false)} />
              </CloseIconWrapper>
            </Header>
            <BottomContent>
              <LinkBackWrapper show={backTitle ? true : false}>
                <LinkBack
                  label={backTitle}
                  clickHandler={() => {
                    if (backTitle) {
                      setSelected(all.slice(0, all.length - 1));
                    }
                  }}
                />
              </LinkBackWrapper>
              <Title>
                <Typography component="h2">{parentTitle}</Typography>
              </Title>
              <CategoriesListMobile
                catalogGenders={filters?.gender || []}
                selected={selected}
                queryParameters={validQueryParameters}
                categorySelectHandler={(selected: string[]) => {
                  setSelected(selected ? (selected as string[]) : []);
                }}
                cmsCollectionTags={cmsCollectionTags}
                facetFoundValues={facetFound?.values || []}
                all={all}
              />
            </BottomContent>
            <Buttons>
              <ButtonsContainer>
                <Btn onClick={resetCategories} variant="outlined">
                  <BtnTypography datatype="body_1">
                    {t('catalog:cta.remove-categories').toUpperCase()}
                  </BtnTypography>
                </Btn>
                <Btn onClick={showResults}>
                  <BtnTypography datatype="body_1">
                    {t('catalog:cta.view-results').toUpperCase()}
                  </BtnTypography>
                </Btn>
              </ButtonsContainer>
            </Buttons>
          </CategoriesDrawerContent>
        </OuterDrawerContent>
      </Drawer>
    </>
  );
};
export default FiltersPanelMobile;

const initCategoriesSelected = (slug: string[]): string[] => {
  return slug && slug.length > 0 ? (slug as string[]) : [];
};

const initFiltersSelected = (
  queryParameters: {
    [key: string]: string | string[] | undefined;
  },
  cmsCollectionTagsFilters: ICmsTagsGroup,
  currencySymbol: string
): { [k: string]: { slug: string; label: string } } | null => {
  let selectedFilters: {
    [k: string]: { slug: string; label: string };
  } = {};

  if (queryParameters && cmsCollectionTagsFilters) {
    Object.keys(queryParameters).forEach(queryParam => {
      if (queryParam) {
        selectedFilters[queryParam] = {
          slug: queryParameters[queryParam] as string,
          label: getLabel(
            queryParameters[queryParam] as string,
            cmsCollectionTagsFilters[queryParam],
            undefined,
            queryParam,
            currencySymbol
          ),
        };
      }
    });
  }

  return Object.keys(selectedFilters).length === 0 ? null : selectedFilters;
};
